import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Card } from "@/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/ui/collapsible";
import { Separator } from "@/ui/separator";
import { Skeleton } from "@/ui/skeleton";
import { Spinner } from "@/ui/spinner";
import { ICONES, Icone, diaDe, hora, mmss, s } from "../visual.jsx";
import { useAcaoDeProva, useAcoes, useCelular, useEstado, useOcupado } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import * as api from "../api.js";
import { Botao, Pontinhos } from "./comuns.jsx";
import { PlayerDeVoz, PreviaDeAnexo } from "./Anexo.jsx";
import { GestosDaBolha, MenuDaBolha, SeloDaReacao, SetaDoMenu } from "./AcoesDaMensagem.jsx";
import BuscaNaConversa from "./BuscaNaConversa.jsx";
import MarcoDoCaso from "./MarcoDoCaso.jsx";
import { marcosDaTrilha } from "../marcos.js";
import SelecaoDeMensagens from "./SelecaoDeMensagens.jsx";
import { naBase as estaNaBase, oQueMudou, retrato } from "./rolagem.js";
import {
  agrupar,
  emFaixas,
  emPedacos,
  encurtarEndereco,
  familiaDoAnexo,
  indiceDeCitadas,
  juntarConversa,
  partirPorTermo,
  porDia as porDiaDaConversa,
  presencaEmPalavras,
  resumoDaCitada,
  rotuloCurto,
  rotuloDoDia,
  urlDoAnexo,
} from "../conversa.js";

/* -------------------------------------------------------------------- bolhas */

/**
 * O dia, num chip que acompanha a rolagem.
 *
 * `sticky` e não um ouvinte de rolagem: o chip do dia corrente fica preso no
 * alto da conversa e o dia seguinte o EMPURRA para fora quando chega — a troca
 * acontece na composição do navegador, sem um quadro de atraso e sem tremer
 * quando a rolagem é rápida. É o que o aplicativo faz, e a versão em JavaScript
 * disso (medir posição a cada evento de scroll) é justamente a que engasga.
 *
 * O embrulho por dia mora em `conversa.js` (`porDia`): sem ele o chip descolaria
 * ao fim do primeiro bloco do dia, e não no fim do dia.
 *
 * É também o marco que o minimapa usa — daí os `data-marco-*`.
 */
function Divisor({ diaIso, quantas }) {
  return (
    <div
      data-marco-dia={diaIso}
      data-marco-rotulo={rotuloCurto(diaIso)}
      data-marco-conta={quantas}
      /* `z-3` fica ACIMA das bolhas e ABAIXO da barra de seleção (z-8) e do
         painel: o chip passa por cima da conversa, nunca por cima de ação. */
      /* O respiro de cima é PADDING, não margem: margem fica FORA do elemento
         grudado e abria um vão entre "o chip do dia que acabou saiu" e "o do dia
         seguinte chegou" — a faixa de rolagem em que a conversa ficava sem data
         nenhuma no topo. Como padding, o chip encosta no topo do embrulho e a
         troca é contínua. */
      className="sticky top-0 z-3 mb-[18px] flex scroll-mt-3 justify-center pt-1.5"
    >
      <span className="rounded-full border border-border/70 bg-card/95 px-3 py-1 text-selo font-semibold tracking-[.08em] whitespace-nowrap text-muted-foreground shadow-sm backdrop-blur-sm">
        {rotuloDoDia(diaIso)}
      </span>
    </div>
  );
}

/**
 * Onde a leitura parou: a linha "não lidas" do aplicativo.
 *
 * É a resposta de quem volta do almoço com nove mensagens novas — sem ela, a
 * única forma de saber onde recomeçar era lembrar a última que tinha lido.
 * Nasce no instante em que a conversa abre e não se recalcula: abrir marca tudo
 * como lido, e um divisor que dependesse do banco viveria um quadro.
 */
function DivisorDeNaoLidas({ quantas }) {
  return (
    <div className="mt-2.5 mb-4 flex items-center gap-2.5" style={s("animation:vFade var(--t-curta) var(--e-entra) both")}>
      <Separator className="flex-1 bg-terra/35" />
      <Badge className="h-auto rounded-etiqueta border-terra/28 bg-terra/10 px-[9px] py-[3px] text-selo font-bold tracking-[.08em] whitespace-nowrap text-terra-escura">
        {quantas} NÃO LIDA{quantas > 1 ? "S" : ""}
      </Badge>
      <Separator className="flex-1 bg-terra/35" />
    </div>
  );
}

/**
 * O texto da mensagem, com os endereços clicáveis e o achado da busca em realce.
 *
 * O realce é aplicado DENTRO dos pedaços de texto, nunca sobre o link: marcar
 * meia URL quebraria o rótulo curto do endereço, que é o que se lê ali.
 */
function Texto({ valor, escuro, termo }) {
  const pedacos = emPedacos(valor);
  return (
    <span style={s("white-space:pre-wrap")}>
      {pedacos.map((p, i) =>
        p.tipo === "link" ? (
          <a
            key={i}
            href={p.href}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => e.stopPropagation()}
            data-dica={p.valor}
            /* `terra-escura` e não `terra` no claro: o link deita sobre a bolha
               de saída, onde o terra dá 4,09:1 — reprovava na auditoria de
               contraste no celular. Sobre a bolha escura o creme já passa. */
            className={`underline underline-offset-2 [overflow-wrap:anywhere] ${
              escuro ? "text-sobre-escuro decoration-sobre-escuro/45" : "text-terra-escura decoration-terra-escura/45"
            }`}
          >
            {encurtarEndereco(p.valor)}
          </a>
        ) : termo ? (
          partirPorTermo(p.valor, termo).map((t, j) =>
            t.marcado ? (
              <mark
                key={`${i}-${j}`}
                className={`rounded-[3px] px-px text-inherit ${escuro ? "bg-ambar/55" : "bg-ambar/40"}`}
              >
                {t.valor}
              </mark>
            ) : (
              <span key={`${i}-${j}`}>{t.valor}</span>
            ),
          )
        ) : (
          p.valor
        ),
      )}
    </span>
  );
}

/* ------------------------------------------------------------- pele da bolha */

/**
 * A pele da bolha, num lugar só.
 *
 * Eram quatro ternários encadeados dentro da string de estilo: mexer no verde da
 * IA obrigava a reler a linha inteira e acertar a quarta vírgula. São quatro
 * formas e a cor é o que diz qual — então são quatro entradas de tabela.
 */
/*
 * Token e não hexadecimal — mas em `var()`, porque a bolha é pintada por
 * ESTILO: a cor sai desta tabela, não de uma classe.
 *
 * A bolha de saída é a superfície escura da mesa (`escuro`/`sobre-escuro`),
 * a mesma do trilho — e não `tinta`, que inverte no escuro e faria a bolha
 * nossa virar clara. A nota é âmbar sobre a MESA, com fio tracejado a 40%: o
 * véu de 32% sobre transparente sumia contra o fundo da conversa.
 */
const PELES = {
  entrada: { fundo: "var(--card)", letra: "var(--foreground)", borda: "var(--border)" },
  saida: { fundo: "var(--color-escuro)", letra: "var(--color-sobre-escuro)", borda: "var(--color-escuro)" },
  ia: { fundo: "var(--color-bolha-ia)", letra: "var(--color-sobre-escuro)", borda: "var(--color-verde)" },
  nota: {
    fundo: "color-mix(in oklab, var(--color-ambar) 13%, var(--color-mesa))",
    letra: "var(--color-ambar-escuro)",
    borda: "color-mix(in oklab, var(--color-ambar) 40%, transparent)",
    tracejada: true,
  },
};

const peleDe = ({ nota, daIA, saida }) => (nota ? PELES.nota : daIA ? PELES.ia : saida ? PELES.saida : PELES.entrada);

/* A largura da mídia na conversa. Ela decide o tamanho da bolha que a contém,
   então é uma constante e não um número solto em três arquivos. */
export const LARGURA_MIDIA = 320;
/** Figurinha: a medida que o próprio WhatsApp usa na conversa. */
export const LARGURA_FIGURINHA = 148;

/* Os tiques, como no aplicativo: um é "saiu", dois é "chegou", azul é "leu".
   `na_fila` é nosso: a mensagem existe no CRM esperando aprovação humana.

   O padrão é "?" e não "✓✓": `entrega` também guarda "cancelada", "agendada
   para AAAA-MM-DD" e o estado cru do provedor — e desenhar os dois
   tiques de entregue para uma mensagem que não saiu é mentira sobre a única
   coisa que a pessoa quer saber olhando ali. */
const TIQUES = {
  /* `ensaio` é a mensagem que ficou só no CRM (sem credencial, WhatsApp caído):
     tique de alerta, e não os dois tiques de entregue, que seriam mentira. */
  ensaio: "⚠",
  na_fila: "⏱",
  /* `saindo` é o eco: a bolha existe na tela e o servidor ainda não confirmou. */
  saindo: "⏱",
  enviada: "✓",
  entregue: "✓✓",
  lida: "✓✓",
  falhou: "!",
  cancelada: "✕",
  /* A agendada guarda a data no próprio valor (`agendada para 2026-08-25`): o
     tique é a ampulheta, e a data vai na dica. */
  agendada: "⏱",
};

/* E o que o tique quer dizer, em frase. A dica mostrava o valor do banco: passar
   o mouse numa resposta esperando aprovação dizia "na_fila". */
const ENTREGA = {
  enviada: "Saiu do CRM",
  entregue: "Chegou no celular do cliente",
  lida: "O cliente leu",
  na_fila: "Na fila de Automação, esperando alguém aprovar",
  saindo: "Saindo agora — o servidor ainda não confirmou",
  falhou: "Não saiu — veja o recado",
  cancelada: "Cancelada antes de sair",
  ensaio: "Ficou só no CRM: o WhatsApp estava desconectado",
};

/** O estado como chave do mapa: "agendada para 2026-08-25" é "agendada". */
const chaveDaEntrega = (entrega) => (String(entrega ?? "").startsWith("agendada") ? "agendada" : (entrega ?? "enviada"));

/**
 * O tique da mensagem que saiu — um só, para a bolha e para a galeria.
 *
 * A galeria desenhava "✓✓" fixo: as cinco fotos que ficaram em ensaio, na fila
 * ou que falharam apareciam como entregues no celular do cliente. Um componente
 * só é o que impede a segunda cópia de mentir de novo.
 */
function Tique({ entrega, eco = false }) {
  const chave = chaveDaEntrega(entrega);
  const alerta = chave === "ensaio" || chave === "falhou";
  return (
    <span
      /* A cor é a da HORA ao lado (herdada), e não tinta-fraca fixa: sobre a
         bolha escura o tique em tinta-fraca dava ~2:1 e não se lia. Lida ganha
         o verde-claro, e a troca de cor é transição — o "leu" chega sem pular. */
      className={`inline-block text-selo tracking-[-.1em] transition-colors duration-(--t-curta) ease-entra ${
        alerta ? "text-terra-clara" : chave === "lida" ? "text-verde-claro" : ""
      }`}
      style={s(
        /* O pulinho é `vPop` (index.css) e só entra quando o estado VIRA
           "enviada" na bolha de verdade: a propriedade sai de nada para
           animação, e é essa troca que dispara. */
        chave === "saindo" ? "animation:vPulse 1.2s infinite" : chave === "enviada" && !eco ? "animation:vPop var(--t-curta) var(--e-entra)" : "",
      )}
      data-dica={
        chave === "agendada"
          ? `Agendada — sai em ${String(entrega).replace(/^agendada para /, "")}`
          : (ENTREGA[chave] ?? (entrega ? `Estado do envio: ${entrega}` : ENTREGA.enviada))
      }
    >
      {/* Sem estado gravado a mensagem saiu e ninguém confirmou: um tique,
          igual ao que a dica diz. */}
      {TIQUES[chave] ?? "?"}
    </span>
  );
}

const GRANDE = "18px";
const COLADO = "6px";

/**
 * A geometria do agrupamento.
 *
 * Um bloco é uma peça só: o topo abre na primeira bolha e fica reto nas
 * seguintes, e a pontinha do lado de quem fala ancora a pilha na pessoa.
 */
function raioDe({ nota, saida, primeira }) {
  if (nota) return "14px";
  /* A pontinha fica no canto de baixo do lado de quem fala e nunca sai: é ela
     que ancora a pilha inteira na pessoa. O topo é que abre — redondo na
     primeira, reto nas seguintes, para as bolhas parecerem uma peça. */
  const topo = primeira ? GRANDE : COLADO;
  return saida ? `${GRANDE} ${topo} ${COLADO} ${GRANDE}` : `${topo} ${GRANDE} ${GRANDE} ${COLADO}`;
}

/**
 * Sombra: só a última bolha do bloco levanta.
 *
 * Com sombra em todas, um bloco de seis linhas virava uma pilha suja — cada
 * sombra caindo na bolha de baixo. A pilha tem uma sombra, embaixo, como um
 * cartão único; as de dentro ficam rentes.
 */
const sombraDe = ({ nota, ultima, saida }) =>
  nota || !ultima
    ? "none"
    : saida
      ? "0 3px 12px color-mix(in oklab, var(--color-foreground) 12%, transparent)"
      : "0 1px 4px color-mix(in oklab, var(--color-foreground) 4%, transparent)";

/* ------------------------------------------------------------------- citada */

/**
 * A mensagem citada, dentro da bolha que responde a ela.
 *
 * O banco guarda `responde_a` desde que o Baileys chegou e a tela nunca mostrou:
 * o cliente respondia "esse mesmo" a uma pergunta de três dias antes e a
 * conversa ficava sem pé nem cabeça. Clicar leva à original e ela pisca — é o
 * gesto do aplicativo, e é o que transforma a citação em navegação.
 */
/**
 * O que a máquina leu da mídia, embaixo dela.
 *
 * Áudio e imagem chegam com o texto lido (`transcricao`): a voz vira palavra em
 * `src/transcricao.ts`, o documento fotografado vira texto em `src/visao.ts`. Isso
 * é o que faz a conversa ser LIDA em vez de ouvida — e o que a busca acha quando
 * o que interessa foi dito num áudio de dois minutos.
 *
 * Fica ABAIXO do player e da foto, e não no lugar da legenda: a transcrição não é
 * o que a pessoa escreveu, é leitura de máquina, e misturar as duas fazia a bolha
 * mentir sobre quem disse aquilo. Por isso a régua fina em cima, o rótulo e a
 * tinta mais fraca — hierarquia de nota de rodapé, que é o papel dela.
 *
 * Longa, nasce dobrada em três linhas: um áudio de dois minutos vira um parágrafo
 * que empurra a conversa inteira para fora da tela, e quem rola procurando a
 * última mensagem passaria por cima dele toda vez.
 */
const LIMITE_DOBRADO = 190;

/**
 * Mídia que ficou sem leitura de máquina: o botão que pede a leitura.
 *
 * Sem ele, áudio sem transcrição era um beco: a única saída era rodar um comando
 * no servidor. E o beco é comum — o serviço cai, a cota estoura, a conversa veio
 * importada de um export. Só para áudio e imagem, que é o que a casa sabe ler.
 */
function PedirTranscricao({ mensagem, familia, escuro, meta }) {
  const { transcreverMidia } = useAcoes();
  const ocupado = useOcupado();
  const lendo = ocupado === "mensagem";
  return (
    /* A hora mora NESTA linha: como selo sobre a foto ela caía em cima do botão,
       que também fica no pé da bolha. */
    <div className="mt-[7px] flex items-center justify-between gap-2.5">
      {/* O botão mini da casa: contorno na bolha clara, "sobre escuro" na nossa.
          Era um `Button` cru com o hover escrito à mão para não virar retângulo
          branco sob a letra creme — a variante já resolve isso. */}
      <Botao
        rotulo={lendo ? "LENDO…" : familia === "imagem" ? "LER A IMAGEM" : "TRANSCREVER"}
        icone={familia === "imagem" ? ICONES.nota : ICONES.microfone}
        variante={escuro ? "sobreEscuro" : "normal"}
        tamanho="mini"
        desabilitado={lendo}
        titulo={familia === "imagem" ? "Ler o texto desta imagem" : "Transcrever este áudio"}
        aoTocar={() => transcreverMidia(mensagem.id)}
        classe="alvo tracking-[.04em]"
      />
      {meta}
    </div>
  );
}

function TranscricaoDaMidia({ texto, familia, escuro, termo }) {
  const [aberta, setAberta] = useState(false);
  const limpo = String(texto ?? "").trim();
  if (!limpo) return null;

  const longa = limpo.length > LIMITE_DOBRADO;
  /* Rótulo pelo que a máquina fez: ouvir e ler são leituras diferentes, e quem
     confere a peça precisa saber de qual delas veio a frase. */
  const rotulo = familia === "imagem" ? "TEXTO NA IMAGEM" : "TRANSCRIÇÃO";
  const glifo = familia === "imagem" ? ICONES.nota : ICONES.microfone;
  const tinta = escuro ? "text-sobre-escuro/66" : "text-tinta-suave";
  const risco = escuro ? "border-t-sobre-escuro/20" : "border-t-foreground/12";

  const corpo = (
    <div
      className={`text-corpo leading-[1.5] whitespace-pre-wrap [overflow-wrap:anywhere] ${tinta}`}
      /* `data-transcricao` é a marca que a conferência de tela procura: bolha de
         áudio sem ela é conversa que voltou a ser só som. */
      data-transcricao={familia}
    >
      <Texto valor={limpo} escuro={escuro} termo={termo} />
    </div>
  );

  return (
    <div className={`mt-2 border-t pt-[7px] ${risco}`}>
      <div className={`mb-1 flex items-center gap-1.5 ${tinta}`}>
        <Icone de={glifo} tamanho={14} cor="currentColor" />
        <span className="text-selo font-bold tracking-[.08em]">
          {rotulo}
        </span>
      </div>
      {longa ? (
        /* `Collapsible` do shadcn, e não um `{aberta && …}` à mão: o gatilho ganha
           `aria-expanded` e aponta para o painel, então quem usa leitor de tela
           sabe que "ver tudo" abre ESTE texto. Fechado, as três primeiras linhas
           ficam à vista — dobrar sem prévia esconde justamente a frase que faz
           alguém decidir abrir. */
        <Collapsible open={aberta} onOpenChange={setAberta}>
          {!aberta && (
            <div
              className={`line-clamp-3 text-corpo leading-[1.5] whitespace-pre-wrap [overflow-wrap:anywhere] ${tinta}`}
              data-transcricao={familia}
            >
              {limpo}
            </div>
          )}
          <CollapsibleContent>{corpo}</CollapsibleContent>
          <CollapsibleTrigger
            className={`alvo mt-1 cursor-pointer bg-transparent p-0 text-selo font-bold tracking-[.08em] underline underline-offset-2 ${tinta}`}
          >
            {aberta ? "VER MENOS" : "VER TUDO"}
          </CollapsibleTrigger>
        </Collapsible>
      ) : (
        corpo
      )}
    </div>
  );
}

function Citada({ mensagem, escuro, aoIr }) {
  const nossa = mensagem.direcao === "saida";
  return (
    /* Raio 10 (o do controle), rótulo em selo e texto em apoio: é um botão
       dentro da bolha, e tinha raio 9 com dois tamanhos de letra soltos. */
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        aoIr(mensagem.id);
      }}
      data-dica="Ir para a mensagem citada"
      className={`mb-[7px] block w-full cursor-pointer rounded-controle border-0 border-l-[3px] border-solid px-[9px] py-1.5 text-left transition-colors ${
        escuro
          ? "border-l-sobre-escuro/90 bg-sobre-escuro/12 hover:bg-sobre-escuro/20"
          : "border-l-verde-fundo bg-verde-fundo/8 hover:bg-verde-fundo/12"
      }`}
    >
      <span className={`block text-selo font-bold tracking-[.08em] ${escuro ? "text-sobre-escuro/90" : "text-verde-fundo"}`}>
        {nossa ? "VOCÊ" : "O CLIENTE"}
      </span>
      <span
        className={`mt-px block truncate text-apoio leading-[1.4] ${escuro ? "text-sobre-escuro/80" : "text-tinta-suave"}`}
      >
        {resumoDaCitada(mensagem)}
      </span>
    </button>
  );
}

/* --------------------------------------------------------------- eco falhado */

/**
 * O rodapé do que NÃO saiu, com o texto ainda na mão.
 *
 * Rede caindo no meio de um envio apagava a bolha e o texto junto — e a pessoa
 * ficava sem saber se o cliente recebeu. Aqui a mensagem continua na conversa,
 * marcada, com as três saídas honestas: repetir, editar (volta ao campo) ou
 * descartar. Nada se perde por conta própria.
 */
function EcoQueFalhou({ eco }) {
  const { tentarEcoDeNovo, voltarEcoAoCampo, descartarEco } = useAcoes();
  return (
    <div className="mt-[5px] mr-[3px] ml-[3px] flex flex-wrap items-center justify-end gap-1.5">
      <span className="text-apoio font-semibold text-terra-escura">Não saiu: {eco.erro}</span>
      <Mini rotulo="Tentar de novo" aoTocar={() => tentarEcoDeNovo(eco.id)} forte />
      <Mini rotulo="Editar" aoTocar={() => voltarEcoAoCampo(eco.id)} />
      {/* guarda-ok: pergunta em `descartarEco` (estado.jsx) — é ela que aponta o "Editar". */}
      <Mini rotulo="Descartar" aoTocar={() => descartarEco(eco.id)} />
    </div>
  );
}

/**
 * O rodapé da mensagem que ficou em ensaio.
 *
 * Diferente do eco: o eco falhou AGORA, na tela de quem clicou. Esta ficou
 * gravada com `entrega: "ensaio"` porque o WhatsApp estava desconectado no
 * momento do envio — o aviso apareceu uma vez e depois a bolha era igual a
 * qualquer outra. Quem abria a conversa no dia seguinte não tinha como saber
 * que o cliente nunca recebeu, e não havia como mandar.
 *
 * Um botão só, e por PEDIDO: o texto pode ter envelhecido (cobrança de
 * anteontem que já foi paga), então quem está lendo a conversa decide se ainda
 * vale. Disparar sozinho é o que `AGENTE_AUTOMATICO=0` impede.
 */
function ParouEmEnsaio({ mensagem }) {
  const { reenviarEnsaio } = useAcoes();
  return (
    <div className="mt-[5px] mr-[3px] ml-[3px] flex flex-wrap items-center justify-end gap-1.5">
      <span className="text-apoio font-semibold text-terra-escura">Não chegou ao cliente: o WhatsApp estava fora</span>
      <Mini rotulo="Enviar agora" aoTocar={() => reenviarEnsaio(mensagem.id)} forte />
    </div>
  );
}

/* `forte` é a ação principal (tentar de novo), e é `acao` — terra — porque o
   que ela faz é mandar para o cliente. O resto é contorno. Mini (28) da casa:
   estes botões vivem no pé da bolha. */
function Mini({ rotulo, aoTocar, forte = false }) {
  return <Botao rotulo={rotulo} aoTocar={() => aoTocar()} variante={forte ? "acao" : "normal"} tamanho="mini" />;
}

/**
 * A LINHA QUIETA: o que ocupa o lugar do conteúdo quando ele não está ali
 * (apagada, mídia que não veio, mídia vindo, tipo que o CRM não lê). Eram três
 * desenhos com gap, cor e peso próprios; é um só, para que "não há conteúdo"
 * se leia igual em todo lugar.
 */
const quieta = (escuro) =>
  `flex items-center gap-1.5 text-corpo italic ${escuro ? "text-sobre-escuro/66" : "text-tinta-fraca"}`;

/**
 * O ponto no mapa, como cartão.
 *
 * Chegava assim: "[localização] -28.523714065551758, -49.325016021728516".
 * Dezoito casas decimais que ninguém lê e que não abrem mapa nenhum — e o
 * endereço da empresa reclamada é prova de processo. Aqui vira nome do lugar,
 * coordenada curta e um link que abre o mapa.
 *
 * OpenStreetMap e não Google: é um link, não um script — nenhum dado do cliente
 * sai daqui antes de alguém CLICAR, e não há chave de API para manter viva.
 */
function PontoNoMapa({ local, nome, escuro }) {
  const coordenada = `${Number(local.lat).toFixed(5)}, ${Number(local.lon).toFixed(5)}`;
  const mapa = `https://www.openstreetmap.org/?mlat=${local.lat}&mlon=${local.lon}#map=17/${local.lat}/${local.lon}`;
  return (
    <a
      href={mapa}
      target="_blank"
      rel="noreferrer noopener"
      data-dica="Abrir no mapa"
      className={`flex min-w-[min(240px,100%)] items-center gap-2.5 rounded-cartao border px-[11px] py-[9px] no-underline transition-colors ${
        escuro
          ? "border-sobre-escuro/20 bg-sobre-escuro/8 text-sobre-escuro hover:bg-sobre-escuro/12"
          : "border-foreground/12 bg-foreground/4 text-tinta hover:bg-foreground/8"
      }`}
    >
      <span
        className={`flex size-padrao flex-none items-center justify-center rounded-controle ${
          escuro ? "bg-sobre-escuro/12 text-sobre-escuro" : "bg-terra/12 text-terra-escura"
        }`}
      >
        <Icone de={ICONES.local} tamanho={16} />
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-corpo font-semibold [overflow-wrap:anywhere]">
          {nome?.trim() || local.endereco || "Localização"}
        </span>
        <span className={`font-mono text-selo ${escuro ? "text-sobre-escuro/72" : "text-tinta-fraca"}`}>{coordenada}</span>
      </span>
    </a>
  );
}

/**
 * A bolha do que foi apagado para todos.
 *
 * O WhatsApp escreve "Esta mensagem foi apagada" e o CRM tem de escrever
 * também: num processo, saber que houve mensagem retirada vale tanto quanto o
 * conteúdo dela. Itálico e apagado para não se confundir com fala.
 */
function Apagada({ escuro, nossa }) {
  return (
    <span className={quieta(escuro)}>
      <Icone de={ICONES.proibido} tamanho={14} />
      {nossa ? "Você apagou esta mensagem" : "Esta mensagem foi apagada"}
    </span>
  );
}

/**
 * Mensagem que chegou sem nada para mostrar.
 *
 * O histórico do celular traz a mídia só até um teto de downloads e traz tipos
 * que o CRM não lê (botão, modelo, lista do bot do anúncio): a linha existe, a
 * hora existe, o conteúdo não. Bolha em branco parecia defeito da tela; dizer
 * o que era e onde ver é o que se pode fazer com o que chegou.
 */
const SEM_CONTEUDO = {
  audio: "Áudio que não veio para o CRM",
  image: "Foto que não veio para o CRM",
  video: "Vídeo que não veio para o CRM",
  document: "Documento que não veio para o CRM",
};

/**
 * A mídia do histórico que ainda não veio: pede quando a bolha entra na tela.
 *
 * A importação guarda só a referência — baixar oito mil áudios no pareamento é
 * disco e tempo para o que ninguém vai abrir. Quando chega, o evento da conversa
 * recarrega a trilha e a bolha vira o player/a foto de sempre.
 */
function MidiaSobDemanda({ mensagem, escuro }) {
  const ref = useRef(null);
  const [estado, setEstado] = useState("esperando");
  const [erro, setErro] = useState("");
  const pedir = useCallback(() => {
    setEstado("baixando");
    api.baixarMidia(mensagem.record_id, mensagem.id).catch((e) => {
      setErro(e.message);
      setEstado("falhou");
    });
  }, [mensagem.record_id, mensagem.id]);
  useEffect(() => {
    const el = ref.current;
    if (!el || estado !== "esperando") return;
    const olho = new IntersectionObserver(
      (vistos) => {
        if (vistos.some((v) => v.isIntersecting)) {
          olho.disconnect();
          pedir();
        }
      },
      { rootMargin: "300px 0px" },
    );
    olho.observe(el);
    return () => olho.disconnect();
  }, [estado, pedir]);
  const nome = { audio: "áudio", image: "foto", video: "vídeo", document: "documento" }[mensagem.tipo] ?? "arquivo";
  return (
    <span ref={ref} className={`${quieta(escuro)} flex-wrap`}>
      {estado === "falhou" ? (
        <>
          <Icone de={ICONES.risco} tamanho={14} />
          <span className="min-w-0 flex-1" data-dica={erro}>
            O {nome} não baixou
          </span>
          <Button
            variant="ghost"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              pedir();
            }}
            /* Fantasma mini (28, raio 10). O `Botao` da casa não serve: o toque
               não pode subir para a bolha, que também escuta clique. */
            className={`alvo h-mini rounded-controle px-2.5 text-apoio font-semibold not-italic ${escuro ? "text-sobre-escuro hover:bg-sobre-escuro/12 hover:text-sobre-escuro" : "text-foreground hover:bg-foreground/8"}`}
          >
            Tentar de novo
          </Button>
        </>
      ) : (
        <>
          <Spinner className="size-3.5" />
          <span>Trazendo o {nome} do WhatsApp…</span>
        </>
      )}
    </span>
  );
}

function SemConteudo({ tipo, escuro }) {
  return (
    <span className={quieta(escuro)} data-dica="Está no celular do escritório">
      <Icone de={ICONES.whatsapp} tamanho={14} />
      {SEM_CONTEUDO[tipo] ?? "Mensagem que o CRM não conseguiu ler"}
    </span>
  );
}

/**
 * Contato sem vCard — o que sobrou das mensagens antigas.
 *
 * O cartão completo (telefone, organização, salvar) vem do `.vcf` guardado na
 * pasta do cliente. As mensagens que chegaram antes disso têm só o nome, e
 * "[contato] Fulano" em texto cru não é cartão nenhum.
 */
function ContatoSemCartao({ nome, escuro }) {
  return (
    <span className={`flex items-center gap-[9px] text-corpo font-semibold ${escuro ? "text-sobre-escuro" : "text-tinta"}`}>
      <span
        className={`flex size-mini flex-none items-center justify-center rounded-controle ${
          escuro ? "bg-sobre-escuro/12" : "bg-foreground/8"
        }`}
      >
        <Icone de={ICONES.equipe} tamanho={16} />
      </span>
      {nome}
    </span>
  );
}

/**
 * A enquete, como o cliente a vê.
 *
 * O histórico guardava "📊 pergunta / • opção / • opção" em texto — legível,
 * mas mudo: não dizia QUEM respondeu o quê, que é a única razão de mandar
 * enquete num atendimento ("qual o melhor horário?"). Aqui cada opção tem barra
 * e contagem, e os votos chegam sozinhos pelo canal de eventos.
 */
function CartaoDeEnquete({ enquete, escuro }) {
  const votos = enquete.votos ?? enquete.opcoes.map((opcao) => ({ opcao, votos: 0 }));
  const total = votos.reduce((n, o) => n + (o.votos ?? 0), 0);
  const claro = escuro ? "text-sobre-escuro/72" : "text-tinta-fraca";
  const trilho = escuro ? "bg-sobre-escuro/20" : "bg-foreground/8";
  const barra = escuro ? "bg-verde-claro" : "bg-verde";

  return (
    <div className="min-w-[min(268px,100%)]">
      <div className="mb-0.5 flex items-center gap-[7px]">
        <Icone de={ICONES.enquete} tamanho={14} />
        <span className={`text-selo font-bold tracking-[.08em] ${claro}`}>
          {enquete.multipla ? "ESCOLHA QUANTAS QUISER" : "ESCOLHA UMA OPÇÃO"}
        </span>
      </div>
      <div className="mb-2.5 text-corpo leading-[1.4] font-semibold [overflow-wrap:anywhere]">
        {enquete.pergunta}
      </div>

      {votos.map((o) => {
        const fatia = total ? (o.votos ?? 0) / total : 0;
        return (
          <div key={o.opcao} className="mb-[9px]">
            <div className="mb-1 flex justify-between gap-2.5 text-corpo">
              <span className="min-w-0 [overflow-wrap:anywhere]">{o.opcao}</span>
              <span className={`flex-none font-mono text-selo ${claro}`}>{o.votos ?? 0}</span>
            </div>
            <div className={`h-[5px] overflow-hidden rounded-full ${trilho}`}>
              {/* `scaleX` e não largura: animar largura refaz o layout da bolha
                  a cada voto que chega. */}
              <div
                className={`size-full origin-left transition-transform duration-(--t-media) ease-entra ${barra}`}
                style={s(`transform:scaleX(${fatia})`)}
              />
            </div>
            {/* Quem votou, quando o aparelho conta: numa enquete de atendimento
                "duas pessoas escolheram tarde" vale menos que o nome delas. */}
            {(o.quem ?? []).length ? (
              <div className={`mt-[3px] font-mono text-selo ${claro}`}>
                {o.quem.join(", ")}
              </div>
            ) : null}
          </div>
        );
      })}

      <div className={`mt-0.5 text-selo ${claro}`}>
        {total ? `${total} voto(s)` : "Ninguém votou ainda — a resposta chega sozinha."}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- bolha */

/**
 * Uma mensagem dentro do bloco. Quatro formas na mesma bolha: entrada, saída,
 * nota interna e resposta da IA — e é a cor que diz qual, porque quem lê rápido
 * não lê rótulo.
 */
const Bolha = memo(function Bolha({
  mensagem,
  quem,
  primeira,
  ultima,
  anexo,
  anexos,
  acao,
  citada,
  termo,
  apontada,
  selecao,
  aoIr,
}) {
  const nota = mensagem.tipo === "note";
  const saida = mensagem.direcao === "saida";
  const daIA = Boolean(quem.ia);
  /*
   * A transcrição NÃO é o texto da mensagem.
   *
   * Ela entrava aqui como queda (`texto ?? transcricao`), e a bolha passava a
   * dizer, com a tipografia de quem falou, uma frase que a máquina leu — sem
   * player à vista em cima e sem dizer de onde veio. Legenda é legenda;
   * transcrição desce para o bloco próprio, abaixo da mídia.
   */
  const texto = mensagem.texto ?? "";
  const transcrito = String(mensagem.transcricao ?? "").trim();
  const forma = { nota, saida, daIA, primeira, ultima };
  const pele = peleDe(forma);
  /* Mídia sem legenda não leva moldura: a bolha É a mídia, e 12px de fundo em
     volta de uma foto só faz a foto parecer pequena e mal cortada. */
  const soMidia = Boolean(anexo) && !texto && !transcrito;
  /*
   * Figurinha é solta e pequena, como no aplicativo.
   *
   * Sem isto ela vinha do tamanho de uma foto (320px) dentro de uma bolha com
   * fundo — um "👍" ocupando meia conversa, e a figurinha branca sumindo no
   * fundo claro. Aqui ela perde bolha, sombra e moldura: fica só o desenho.
   */
  const figurinha = mensagem.tipo === "sticker" && Boolean(anexo);
  /* O ponto que o cliente mandou. As coordenadas vêm em `dados` (coluna própria
     desde que a localização deixou de ser um texto ilegível na bolha). */
  const local = mensagem.tipo === "location" && mensagem.dados?.lat != null ? mensagem.dados : null;
  /* A enquete que o escritório mandou, com a contagem que o aparelho devolveu. */
  const enquete = mensagem.tipo === "poll" && mensagem.dados?.enquete?.opcoes ? mensagem.dados.enquete : null;
  /*
   * "Apagada para todos" — e dito com todas as letras.
   *
   * `marcarApagada` limpa o texto e carimba a hora: a bolha continua na trilha
   * (é registro de que ALGO foi dito e retirado, o que importa num processo),
   * mas sem uma palavra ela virava um retângulo vazio que parecia defeito.
   */
  const apagada = Boolean(mensagem.apagado_em);
  /* Cartão de contato que veio antes do vCard ser guardado: sobrou o nome. */
  const contatoSimples = mensagem.tipo === "contact" && !anexo ? (mensagem.dados?.nome ?? texto) : null;
  const vazia = !apagada && !anexo && !texto.trim() && !transcrito && !local && !enquete && !contatoSimples && !nota;
  const raio = raioDe(forma);
  /*
   * NOTA é bolha clara, mesmo saindo do escritório ou da IA.
   *
   * `escuro` decide a cor do que é pintado por cima do fundo — link, marcação da
   * busca, tique. A nota do agente entra como saída (e às vezes como IA), mas a
   * pele dela é o papel âmbar: sem esta ressalva o link saía em creme sobre
   * fundo claro e virava texto branco ilegível — foi assim que os três links de
   * assinatura apareceram invisíveis no vídeo de 23/08.
   */
  const escuro = (saida || daIA) && !nota;
  const eco = Boolean(mensagem.eco);
  const falhou = eco && mensagem.entrega === "falhou";
  /* Gravada e NÃO enviada: a bolha existe, o cliente não recebeu. Só na saída —
     `ensaio` não acontece em mensagem que chega. */
  const emEnsaio = saida && !nota && mensagem.entrega === "ensaio";
  const marcada = Boolean(selecao?.ativa && selecao.marcadas.includes(mensagem.id));

  /*
   * A hora, o "editada" e o tique moram DENTRO da bolha, no canto de baixo —
   * como no WhatsApp.
   *
   * Estavam numa linha abaixo dela, e só na última do bloco: as do meio
   * mostravam a hora num selo fora da bolha, no hover. Duas geometrias para o
   * mesmo dado, e a mais usada (a do meio) invisível. Aqui é uma só, em toda
   * bolha: `float` deixa o texto correr em volta e a hora encaixa na última
   * linha quando sobra vão, exatamente como no aplicativo.
   */
  /* Mídia que ainda pede leitura tem o botão no pé da bolha: a hora vai para
     a linha dele, não por cima. */
  const pedeLeitura =
    Boolean(anexo) && !apagada && !transcrito && !figurinha && ["audio", "imagem"].includes(familiaDoAnexo(anexo.nome));
  const sobreMidia = (soMidia || figurinha) && !pedeLeitura;
  /* Creme a 62% na bolha escura (era 58%, e o tique ao lado não passava de
     3:1); a mono da casa, e não JetBrains fixa. */
  const corDaHora = escuro ? "text-sobre-escuro/62" : "text-tinta-fraca";
  const meta = (
    <span
      className={`inline-flex items-center gap-1 font-mono text-selo whitespace-nowrap ${
        pedeLeitura
          ? corDaHora
          : sobreMidia
            ? /* Sem texto para dividir a linha, a hora vira selo por cima da
                 mídia: é o que o aplicativo faz, e é o que a mantém legível
                 sobre foto clara. `escuro` e não `foreground`: o selo é escuro
                 nos dois temas, e o foreground inverte. */
              "pointer-events-none absolute right-[9px] bottom-2 z-[2] rounded-controle bg-escuro/72 px-[7px] py-0.5 text-sobre-escuro"
            : `float-right mt-[7px] -mr-0.5 -mb-0.5 ml-2.5 ${corDaHora}`
      }`}
    >
      {mensagem.editado_em && <span className="italic">editada</span>}
      {hora(mensagem.quando)}
      {/* O tique que acabou de virar "enviada" dá um pulinho: é a única
          confirmação de que o servidor respondeu, e sem ela a diferença entre
          "saindo" e "saiu" era um caractere trocado em silêncio. */}
      {saida && !nota && <Tique entrega={mensagem.entrega} eco={eco} />}
    </span>
  );

  return (
    <div
      className={`bolha${selecao?.ativa ? " selecionavel" : ""}${marcada ? " selecionada" : ""}`}
      data-mensagem={mensagem.id}
      /* Em modo seleção a linha inteira é o alvo: é assim no aplicativo, e mirar
         numa caixinha de 18px ao lado de uma bolha é o que faz alguém marcar a
         mensagem errada. Eco (que ainda não existe no servidor) não entra. */
      onClick={selecao?.ativa && !eco ? () => selecao.alternar(mensagem.id) : undefined}
      /* Em modo seleção a bolha É o controle: sem `role`/`tabIndex` quem opera
         por teclado não tinha como marcar nada, e a barra ficava de enfeite. */
      role={selecao?.ativa && !eco ? "checkbox" : undefined}
      aria-checked={selecao?.ativa && !eco ? marcada : undefined}
      tabIndex={selecao?.ativa && !eco ? 0 : undefined}
      onKeyDown={
        selecao?.ativa && !eco
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                selecao.alternar(mensagem.id);
              }
            }
          : undefined
      }
      style={s(
        /* Coluna, e não linha: a bolha e o rodapé são um em cima do outro, e é o
           `align-items` que encosta os dois no lado de quem falou. */
        /* Figurinha respira: sem bolha em volta, 2px colava um desenho no outro
           e três figurinhas viravam uma tira só. */
        `margin-bottom:${figurinha ? "6px" : ultima ? "1px" : "2px"};position:relative;display:flex;flex-direction:column;align-items:${nota ? "center" : saida ? "flex-end" : "flex-start"};scroll-margin:90px 0;${eco && !falhou ? "opacity:.72;" : ""}${selecao?.ativa ? "padding:2px 4px;" : ""}`,
      )}
    >
      {/* A bolha e a reação rápida na mesma linha: a carinha fica FORA da bolha,
          do lado de dentro da conversa, como no aplicativo. */}
      <span
        style={s(
          `display:flex;align-items:center;gap:4px;max-width:100%;min-width:0;flex-direction:${saida ? "row-reverse" : "row"}${selecao?.ativa ? ";pointer-events:none" : ""}`,
        )}
      >
        {/*
          O invólucro segue a LARGURA da bolha, não a da coluna.
          Como bloco, ele esticava até o fim da faixa e a bolha ficava encostada
          na borda esquerda dele: o áudio que o escritório mandou aparecia
          flutuando no meio da conversa, a 126px do lugar onde as outras bolhas
          de saída terminam. Flex com o alinhamento do lado de quem fala põe cada
          bolha na margem certa, seja qual for a largura dela.
        */}
        <span
          style={s(
            `position:relative;min-width:0;max-width:100%;display:flex;flex-direction:column;align-items:${saida ? "flex-end" : "flex-start"}`,
          )}
        >
          {!nota && !eco && !selecao?.ativa && <SetaDoMenu mensagem={mensagem} nossa={saida} escuro={escuro} />}
          {/* O botão direito na bolha abre o mesmo menu da seta: `MenuDaBolha`
              faz a bolha ser o gatilho (sem `<div>` a mais em volta) e devolve
              os itens que já existem no menu. */}
          <MenuDaBolha mensagem={mensagem} nossa={saida} ativo={!nota && !eco && !selecao?.ativa}>
            <div
              className={`text-corpo${apontada ? " piscando" : ""}`}
              style={s(
                /*
                 * Com mídia, é a mídia que manda na largura: a bolha tem a medida do
                 * anexo (`LARGURA_MIDIA`) e a legenda quebra debaixo dele, em vez de a
                 * legenda esticar a bolha e deixar o vídeo pequeno num canto de uma
                 * faixa preta larga. Medida definida, não `fit-content`: o `max-content`
                 * de uma foto de 3000px esticaria a bolha até o limite da coluna.
                 */
                figurinha
                  ? /* Medida em px, e não em %: o invólucro tem a largura do
                       CONTEÚDO, então "60%" resolvia contra ele mesmo e a
                       figurinha encolhia para 89px. */
                    `position:relative;background:transparent;border:none;padding:0;box-shadow:none;width:${LARGURA_FIGURINHA}px;max-width:100%`
                  : `position:relative;background:${pele.fundo};color:${pele.letra};border:1px ${pele.tracejada ? "dashed" : "solid"} ${falhou ? "var(--color-terra)" : pele.borda};border-radius:${raio};padding:${soMidia ? "3px" : nota ? "9px 14px" : "11px 14px"};line-height:1.55;box-shadow:${sombraDe(forma)};overflow-wrap:anywhere;overflow:hidden;${anexo ? `width:min(${LARGURA_MIDIA + (soMidia ? 6 : 28)}px,100%)` : "max-width:100%"}`,
              )}
            >
              {sobreMidia && meta}
              {citada && <Citada mensagem={citada} escuro={escuro} aoIr={aoIr} />}
              {apagada && <Apagada escuro={escuro} nossa={saida} />}
              {vazia &&
                (mensagem.midia_pendente ? (
                  <MidiaSobDemanda mensagem={mensagem} escuro={escuro} />
                ) : (
                  <SemConteudo tipo={mensagem.tipo} escuro={escuro} />
                ))}
              {local && <PontoNoMapa local={local} nome={texto} escuro={escuro} />}
              {contatoSimples && <ContatoSemCartao nome={contatoSimples} escuro={escuro} />}
              {enquete && <CartaoDeEnquete enquete={enquete} escuro={escuro} />}
              {anexo && (
                <div style={s(texto ? "margin-bottom:9px" : "")}>
                  <PreviaDeAnexo
                    anexo={anexo}
                    lista={anexos}
                    acao={acao}
                    escuro={escuro}
                    largura={figurinha ? LARGURA_FIGURINHA : LARGURA_MIDIA}
                    raio={figurinha ? "0" : soMidia ? "15px" : "12px"}
                    limpo={figurinha}
                  />
                </div>
              )}
              {texto && !local && !enquete && !apagada && !contatoSimples && (
                <Texto valor={texto} escuro={escuro} termo={termo} />
              )}
              {anexo &&
                !apagada &&
                (transcrito ? (
                  <TranscricaoDaMidia texto={transcrito} familia={familiaDoAnexo(anexo.nome)} escuro={escuro} termo={termo} />
                ) : (
                  /* Só áudio e imagem: vídeo e PDF a casa não lê, e botão que
                     sempre falha é pior que botão nenhum. */
                  ["audio", "imagem"].includes(familiaDoAnexo(anexo.nome)) && (
                    <PedirTranscricao mensagem={mensagem} familia={familiaDoAnexo(anexo.nome)} escuro={escuro} meta={meta} />
                  )
                ))}
              {!sobreMidia && !pedeLeitura && meta}
            </div>
          </MenuDaBolha>
        </span>
        {!nota && !eco && !selecao?.ativa && <GestosDaBolha mensagem={mensagem} nossa={saida} />}
      </span>

      {/* A reação recebida cola embaixo da bolha, sobrepondo a borda. */}
      <SeloDaReacao mensagem={mensagem} nossa={saida} />

      {falhou && <EcoQueFalhou eco={mensagem} />}
      {emEnsaio && <ParouEmEnsaio mensagem={mensagem} />}
    </div>
  );
});

/* --------------------------------------------------------------- galeria */

/**
 * A galeria da faixa: foto em grade, áudio e vídeo empilhados — dentro da bolha.
 *
 * A bolha não é enfeite aqui: sem ela, um grupo de áudio + contato ficava
 * flutuando no papel, sem lado nem dono, e o player perdia a tinta (ele herda a
 * cor de quem o contém). Foto é a exceção que confirma: a grade de fotos É a
 * superfície, então a bolha só a emoldura com 3px.
 */
function Galeria({ faixa, anexos, acao, quem, saida, aoAbrirPeticao, provas, selecao }) {
  const tudoFoto = faixa.midia.every((a) => familiaDoAnexo(a.nome) === "imagem");
  const colunas = Math.min(3, faixa.midia.length);
  const ultima = faixa.mensagens[faixa.mensagens.length - 1];
  /* Qual fato, não "existe algum": o botão prometia levar ao fato e só abria a
     petição, deixando a pessoa procurar na coluna o que ela acabou de apontar. */
  const fatoDaqui = (provas ?? []).find((p) => faixa.mensagens.some((m) => m.id === p.mensagem_id)) ?? null;
  const pele = peleDe({ saida, daIA: Boolean(quem.ia) });
  const ids = faixa.mensagens.map((m) => m.id);
  /* Uma galeria é UMA peça na tela: marcá-la marca as mensagens todas que ela
     colapsou, senão o "5 fotos" selecionado encaminharia uma foto só. */
  const marcada = Boolean(selecao?.ativa && ids.every((id) => selecao.marcadas.includes(id)));

  return (
    <div
      /* Marco do minimapa: "onde ele mandou a carteira de trabalho" é a pergunta
         que faz alguém rolar uma conversa de setenta mensagens. */
      data-marco-anexos={faixa.midia.length}
      data-marco-ancora={faixa.chave}
      data-marco-rotulo={`${faixa.midia.length} ${tudoFoto ? "fotos" : "arquivos"}`}
      data-mensagem={ultima.id}
      className={selecao?.ativa ? `selecionavel${marcada ? " selecionada" : ""}` : undefined}
      onClick={selecao?.ativa ? () => ids.forEach((id) => selecao.alternar(id)) : undefined}
      style={s(`margin-bottom:2px;scroll-margin:80px 0;display:flex;justify-content:${saida ? "flex-end" : "flex-start"}`)}
    >
      {/*
        A largura é DEFINIDA aqui, no lado de fora, e não por `fit-content` na
        bolha: com a medida vindo do filho, a foto de 3000px de largura entrava
        na conta do `max-content` e a bolha esticava até o limite da coluna,
        deixando meio metro de branco ao lado da grade.
      */}
      <div
        style={s(
          `width:min(${LARGURA_MIDIA + (tudoFoto ? 8 : 12)}px,100%);min-width:0${selecao?.ativa ? ";pointer-events:none" : ""}`,
        )}
      >
        <div
          style={s(
            `background:${pele.fundo};color:${pele.letra};border:1px solid ${pele.borda};border-radius:${raioDe({ saida, primeira: true })};padding:${tudoFoto ? "3px" : "5px"};box-shadow:${sombraDe({ ultima: true, saida })};width:100%`,
          )}
        >
          <div
            style={
              tudoFoto
                ? s(
                    `display:grid;grid-template-columns:repeat(${colunas},1fr);gap:3px;border-radius:15px;overflow:hidden;width:100%`,
                  )
                : s("display:flex;flex-direction:column;gap:5px;width:100%")
            }
          >
            {faixa.midia.map((a) =>
              tudoFoto ? (
                <div key={a.id} style={s("aspect-ratio:1;min-width:0")}>
                  <PreviaDeAnexo anexo={a} lista={anexos} acao={acao} escuro={saida} encaixar />
                </div>
              ) : (
                /*
                 * Na pilha, cada peça leva a SUA transcrição embaixo — três áudios
                 * seguidos são três falas diferentes, e um bloco de texto só no pé
                 * da galeria não diria qual é de qual. A grade de fotos é a
                 * exceção: ali a peça é um quadrado de 1:1 e texto por baixo
                 * desmancharia a grade; quem quer o texto abre a foto.
                 */
                <div key={a.id} style={s("min-width:0")}>
                  <PreviaDeAnexo anexo={a} lista={anexos} acao={acao} escuro={saida} raio="10px" />
                  {transcricaoDe(faixa.mensagens, a.id) ? (
                    <TranscricaoDaMidia
                      texto={transcricaoDe(faixa.mensagens, a.id)}
                      familia={familiaDoAnexo(a.nome)}
                      escuro={saida}
                    />
                  ) : (
                    ["audio", "imagem"].includes(familiaDoAnexo(a.nome)) && (
                      <PedirTranscricao mensagem={{ id: a.id }} familia={familiaDoAnexo(a.nome)} escuro={saida} />
                    )
                  )}
                </div>
              ),
            )}
          </div>
        </div>

        <div
          className="text-muted-foreground"
          style={s(
            `display:flex;justify-content:${saida ? "flex-end" : "flex-start"};gap:6px;align-items:center;margin:5px 3px 0`,
          )}
        >
          <span className="font-mono text-selo">
            {faixa.midia.length} arquivos · {hora(ultima.quando)}
          </span>
          {/* O estado da ÚLTIMA mídia da faixa — é a que fecha a galeria. */}
          {saida && <Tique entrega={ultima.entrega} />}
          {fatoDaqui && (
            <Badge
              render={<button type="button" />}
              onClick={() => aoAbrirPeticao(fatoDaqui.id)}
              className="h-auto cursor-pointer rounded-etiqueta bg-verde-fundo/12 px-[7px] py-0.5 text-selo font-bold tracking-[.08em] text-verde-fundo hover:bg-verde-fundo/20"
            >
              → FATO NA PETIÇÃO
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A transcrição da mensagem dona daquele anexo, dentro da faixa agrupada.
 *
 * Casamento por id EXATO, sem queda para "a primeira que tiver texto": numa
 * pilha de cinco áudios, uma queda dessas poria a fala de um embaixo do player
 * de outro — e ninguém confere transcrição contra o áudio, é justamente para não
 * precisar ouvir que ela existe. `anexoDe` casa anexo com mensagem pelo mesmo
 * id, então é ele que vale aqui.
 */
const transcricaoDe = (mensagens, anexoId) => mensagens.find((m) => m.id === anexoId)?.transcricao ?? "";

/** Um bloco: avatar de um lado, nome em cima, as bolhas e galerias encostadas. */
function Bloco({ bloco, celular, anexoDe, anexos, provas, acao, aoAbrirPeticao, citadas, termo, apontada, selecao, aoIr, nova }) {
  const { quem, nota, direcao } = bloco;
  const saida = direcao === "saida";
  /* min(78%, 440): em coluna larga 65% dava bolha de 700px, linha longa
     demais para ler; no celular 86%, que é o que sobra depois do respiro. */
  const largo = celular ? "max-w-[86%]" : "max-w-[min(78%,440px)]";
  const faixas = emFaixas(bloco.mensagens, anexoDe);
  /* Bolha que acabou de chegar entra animada; as que já estavam não re-animam a
     cada releitura da trilha (reação, tique, entrega mudam o objeto e não a
     posição — e a conversa inteira piscando a cada evento é o que se via).
     Sobe 8px e acende em 220ms a partir do canto de quem falou; reduzir
     movimento (acesso.css) zera o deslize do tw-animate e deixa só a luz. */
  const entrada = nova
    ? `animate-in fade-in slide-in-from-bottom-2 duration-[220ms] ease-entra ${saida ? "origin-bottom-right" : "origin-bottom-left"}`
    : "";
  const assentada = nova ? "" : "animation:vFade var(--t-media) var(--e-entra) both";

  const peca = (faixa, i) =>
    faixa.midia ? (
      <Galeria
        key={faixa.chave}
        faixa={faixa}
        anexos={anexos}
        acao={acao}
        quem={quem}
        saida={saida}
        provas={provas}
        aoAbrirPeticao={aoAbrirPeticao}
        selecao={selecao}
      />
    ) : (
      <Bolha
        key={faixa.chave}
        mensagem={faixa.mensagem}
        quem={quem}
        primeira={i === 0}
        ultima={i === faixas.length - 1}
        anexo={faixa.anexo}
        anexos={anexos}
        acao={acao}
        citada={faixa.mensagem.responde_a ? (citadas.get(String(faixa.mensagem.responde_a)) ?? null) : null}
        termo={termo}
        apontada={apontada === faixa.mensagem.id}
        selecao={selecao}
        aoIr={aoIr}
      />
    );

  if (nota)
    return (
      <div className={`mb-3 flex justify-center ${entrada}`} style={s(assentada)}>
        <div className="max-w-[520px] min-w-0">{faixas.map(peca)}</div>
      </div>
    );

  /* Sem retrato ao lado da bolha: em conversa de duas pessoas o WhatsApp não
     desenha nenhum, e dois avatares de 30px por bloco eram 60px de coluna que a
     bolha não tinha. Quem falou continua dito — pelo lado e pelo nome acima da
     pilha, que aqui não é enfeite: a mesma saída pode ser da Laura, do Felipe
     ou da IA. */
  return (
    <div
      className={`mb-3.5 flex items-end ${saida ? "justify-end" : "justify-start"} ${entrada}`}
      style={s(assentada)}
    >
      <div className={`min-w-0 ${largo}`}>
        {/* 12,5 (apoio): o nome de quem falou é frase, e a 11 era o menor texto
            da conversa. A cor é da pessoa, calculada, e por isso fica no estilo. */}
        <div
          className={`mt-0 mr-0 mb-1 ml-0.5 truncate text-apoio font-semibold ${saida ? "text-right" : "text-left"}`}
          style={s(`color:${quem.tinta}`)}
        >
          {quem.nome}
        </div>
        {faixas.map(peca)}
      </div>
    </div>
  );
}

/**
 * A reunião gravada, na linha do tempo da conversa.
 *
 * A reunião de alinhamento é parte do atendimento — foi ali que o cliente contou
 * a jornada e o barulho da máquina. Vivendo só numa tela separada, ela sumia da
 * história do caso; aqui ela aparece na hora em que aconteceu, com o áudio para
 * ouvir e a porta para a transcrição no painel.
 */
function CartaoDeReuniao({ gravacao, aoAbrir }) {
  return (
    <div className="mt-1.5 mb-3.5 flex justify-center" style={s("animation:vFade var(--t-media) var(--e-entra) both")}>
      <Card className="w-[min(420px,86%)] gap-0 rounded-cartao border-verde-fundo/28 bg-verde-fundo/8 px-[13px] py-[11px] shadow-none ring-0 [--card-spacing:0px]">
        <div className="flex items-center gap-2">
          <Icone de={ICONES.calls} tamanho={16} cor="var(--color-verde-fundo)" />
          <span className="text-selo font-bold tracking-[.08em] text-verde-fundo">REUNIÃO DE ALINHAMENTO</span>
          <span className="ml-auto font-mono text-selo text-muted-foreground">{mmss(gravacao.duracao_seg ?? 0)}</span>
        </div>
        {gravacao.assunto || gravacao.titulo ? (
          <div className="mt-[5px] text-corpo leading-[1.45] text-foreground">{gravacao.assunto || gravacao.titulo}</div>
        ) : null}
        {gravacao.audio_bytes ? (
          /* O player da casa, o mesmo do recado de voz: velocidade e onda clicável.
             A barra nativa não tem 2x — e é a 2x que se reouve uma reunião de
             quarenta minutos para conferir um valor. */
          <div className="mt-2">
            <PlayerDeVoz
              anexo={{ url: api.audioDaGravacao(gravacao.id), nome: gravacao.audio_arquivo || `reuniao-${gravacao.id}.webm` }}
            />
          </div>
        ) : null}
        <Button
          variant="link"
          size="xs"
          onClick={aoAbrir}
          className="mt-2 h-auto justify-start p-0 text-selo font-bold tracking-[.08em] text-verde-fundo no-underline hover:underline"
        >
          VER TRANSCRIÇÃO E COMBINADOS →
        </Button>
      </Card>
    </div>
  );
}

/* --------------------------------------------------------- topo do histórico */

/**
 * O começo da conversa — ou a porta para o pedaço anterior dela.
 *
 * A rota agregada traz as 200 últimas mensagens. Antes disto, o resto não
 * existia: uma conversa de oito meses abria no meio e nada na tela dizia que
 * havia mais atrás. Agora o topo é uma das três frases honestas — buscando,
 * "tem mais" ou "começa aqui" — e a busca também acontece sozinha quando quem lê
 * chega no topo rolando.
 */
function TopoDoHistorico({ area, temMais, buscando, aoBuscar }) {
  const sentinela = useRef(null);

  useEffect(() => {
    const alvo = sentinela.current;
    if (!alvo || !temMais || typeof IntersectionObserver === "undefined") return undefined;
    const olho = new IntersectionObserver((entradas) => entradas.some((e) => e.isIntersecting) && aoBuscar(), {
      root: area.current,
      /* Começa a buscar 300px ANTES de o topo aparecer: chegar no topo e só
         então esperar a rede é a pausa que faz a rolagem parecer travada. */
      rootMargin: "300px 0px 0px 0px",
    });
    olho.observe(alvo);
    return () => olho.disconnect();
  }, [area, aoBuscar, temMais, buscando]);

  if (!temMais)
    return (
      <div className="pt-1 pb-3.5 text-center text-selo font-semibold tracking-[.08em] text-muted-foreground">
        COMEÇO DA CONVERSA
      </div>
    );

  return (
    <div ref={sentinela} className="flex flex-col items-center gap-2 pt-1.5 pb-3.5" role={buscando ? "status" : undefined}>
      {buscando ? (
        /* Esqueleto e não "carregando…": o que vem são bolhas, e três formas de
           bolha dizem isso sem uma palavra. A altura e o lado são os da bolha
           que vem — é o que faz o histórico ENTRAR em vez de a tela se remontar. */
        <>
          <span className="sr-only">Trazendo as mensagens anteriores…</span>
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              aria-hidden="true"
              className={`rounded-superficie bg-foreground/8 ${i % 2 ? "self-end" : "self-start"}`}
              style={{ height: 34 + (i % 2) * 14, width: i % 2 ? "42%" : "58%" }}
            />
          ))}
        </>
      ) : (
        <Botao rotulo="Carregar mensagens anteriores" aoTocar={aoBuscar} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ presença */

/** "digitando…" com os três pontinhos — o retorno que faz o cliente esperar. */
function LinhaDePresenca({ presenca, nome }) {
  const dito = presencaEmPalavras(presenca);
  if (!dito?.vivo) return null;
  return (
    <div
      aria-live="off"
      className="flex items-center gap-2 pb-2.5 pl-10"
      style={s("animation:vFade var(--t-curta) var(--e-entra) both")}
    >
      {/* O raio da bolha de entrada (18, canto colado em 6): "digitando" é a
          bolha que vem, e com raio próprio parecia outro componente. */}
      <span className="flex items-center rounded-[18px_18px_18px_6px] border border-border bg-card px-[11px] py-2 shadow-[0_1px_4px_color-mix(in_oklab,_var(--color-foreground)_4%,_transparent)]">
        <Pontinhos cor="bg-terra" espaco="gap-1" />
      </span>
      <span className="text-apoio text-muted-foreground">
        {nome} está {dito.texto}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------- pílula de novas */

/**
 * "3 novas mensagens" — quando chega coisa e a leitura está no meio da conversa.
 *
 * A trilha antiga descia para o fim a cada mensagem nova, sempre: quem estava
 * lendo o que o cliente mandou em abril era arrancado de lá por um "bom dia" de
 * hoje. Agora só desce quem já estava no fim; o resto recebe a pílula e decide.
 */
function PilulaDeNovas({ quantas, mostrar, aoTocar }) {
  return (
    /*
     * A centragem mora no INVÓLUCRO e o movimento no botão — nunca no mesmo
     * elemento.
     *
     * A pílula centrava com `-translate-x-1/2` e entrava com `vUp`, que termina
     * em `transform: none`: o último quadro da animação apagava a centragem e a
     * pílula saltava meia largura para a direita, toda vez que aparecia. Era o
     * pulo que se via como "piscada", e nenhum teste o pegaria — o CSS está
     * certo nas duas linhas, o defeito é as duas disputarem `transform`.
     */
    <div className="pointer-events-none absolute bottom-3.5 left-1/2 z-[6] -translate-x-1/2">
      <Button
        onClick={aoTocar}
        /*
         * Montada SEMPRE, escondida por opacidade.
         *
         * Desmontar era a segunda metade da piscada: rolando perto da fronteira
         * (e durante a própria descida suave, que atravessa a fronteira em
         * dezenas de quadros) a pílula nascia e morria em sequência, e cada
         * nascimento reiniciava a animação de entrada do zero. Escondida por
         * opacidade ela também SAI animada, em vez de desaparecer num quadro.
         *
         * `inert` e não só `pointer-events`: escondida ela não pode ser
         * alcançada pelo teclado nem lida pelo leitor de tela — botão invisível
         * que recebe foco é pior que botão que pisca.
         */
        inert={!mostrar}
        aria-hidden={!mostrar}
        className={`pointer-events-auto h-padrao gap-[7px] rounded-full bg-escuro px-3.5 text-apoio font-semibold text-sobre-escuro shadow-[0_12px_28px_-10px_color-mix(in_oklab,_var(--color-escuro)_60%,_transparent)] transition-[opacity,transform] duration-[var(--t-curta)] hover:bg-escuro/90 hover:text-sobre-escuro ${
          mostrar ? "translate-y-0 opacity-100 ease-entra" : "pointer-events-none translate-y-[14px] opacity-0 ease-sai"
        }`}
      >
        {quantas ? `${quantas} nova${quantas > 1 ? "s" : ""}` : "Ir para o fim"}
        <Icone de={ICONES.baixo} tamanho={14} />
      </Button>
    </div>
  );
}

/* ----------------------------------------------------------------- minimapa */

/**
 * O minimapa da conversa: um traço por dia na margem direita, que vira lista de
 * vidro com o ponteiro por perto — o mesmo gesto do minimapa do modal de chamado.
 *
 * Lê o DOM (`[data-marco-dia]`, `[data-marco-anexos]`), como lá: quem desenha o
 * divisor e a galeria decide o que é marco, e o mapa não recebe lista nenhuma —
 * marco novo entra no índice só por existir na tela.
 *
 * O que ele sabe além dos dias: quantas mensagens tem cada dia, onde estão os
 * anexos DENTRO do dia que está sendo lido (é a pergunta real — "onde ele mandou
 * a carteira de trabalho") e se a leitura está longe do fim.
 */
function Minimapa({ area, dependencia }) {
  const [dias, setDias] = useState([]);
  const [ativo, setAtivo] = useState(null);
  const [naBase, setNaBase] = useState(true);

  useEffect(() => {
    const scroller = area.current;
    if (!scroller) return undefined;

    /* Os marcos de agora, guardados fora do estado: `rever()` os acha no DOM e
       `situar()` os mede. Rolar não muda QUAIS marcos existem — só qual deles
       está sendo lido. */
    const marcos = { current: [] };

    /* Que marcos existem. Custa uma varredura do DOM da conversa, então só roda
       quando o conteúdo pode ter mudado: na montagem, quando a trilha cresce
       (`dependencia`) e quando o observador de tamanho avisa. */
    const rever = () => {
      /* Uma consulta só, em ordem de documento: é ela que diz qual anexo está
         dentro de qual dia sem ninguém precisar comparar posição de nó. */
      const nos = Array.from(scroller.querySelectorAll("[data-marco-dia],[data-marco-anexos]"));
      const lidos = [];
      for (const no of nos) {
        if (no.dataset.marcoDia) {
          lidos.push({
            chave: no.dataset.marcoDia,
            rotulo: no.dataset.marcoRotulo,
            conta: Number(no.dataset.marcoConta || 0),
            no,
            anexos: [],
          });
          continue;
        }
        lidos[lidos.length - 1]?.anexos.push({
          chave: `${lidos[lidos.length - 1].chave}-${no.dataset.marcoAncora}`,
          rotulo: no.dataset.marcoRotulo,
          no,
        });
      }
      marcos.current = lidos;

      setDias((antes) =>
        antes.length === lidos.length &&
        antes.every((d, i) => d.chave === lidos[i].chave && d.anexos.length === lidos[i].anexos.length)
          ? antes
          : lidos,
      );
    };

    /* Onde a leitura está. Só medida: um retângulo por dia, e mais nada. */
    const situar = () => {
      const lidos = marcos.current;
      // Ativo = o último dia cujo divisor já passou pela borda de leitura.
      const limite = scroller.getBoundingClientRect().top + 80;
      setAtivo(
        lidos.reduce(
          (escolhido, d) => (d.no.getBoundingClientRect().top <= limite ? d.chave : escolhido),
          lidos[0]?.chave ?? null,
        ),
      );
      setNaBase(scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 240);
    };

    /*
     * Um quadro, uma medida.
     *
     * A conta inteira (varrer o DOM, medir um retângulo por dia, três gravações
     * de estado) rodava a CADA evento de rolagem — e o navegador manda mais
     * eventos de rolagem do que quadros, então numa conversa de trinta dias a
     * roda do mouse virava trinta medições por evento com layout forçado no
     * meio. Era o engasgo de rolar a conversa: a bolha animando enquanto a
     * trilha para para medir. Agora rolar só agenda o quadro seguinte, e o
     * quadro que já estava agendado absorve a rajada.
     */
    let quadro = 0;
    const aoRolar = () => {
      if (quadro) return;
      quadro = requestAnimationFrame(() => {
        quadro = 0;
        situar();
      });
    };

    rever();
    situar();
    scroller.addEventListener("scroll", aoRolar, { passive: true });
    /* O tamanho mudou: pode ter entrado ou saído marco (histórico subindo, anexo
       carregando a miniatura), então aqui as duas contas valem. */
    const olho = new ResizeObserver(() => {
      rever();
      aoRolar();
    });
    olho.observe(scroller);
    return () => {
      cancelAnimationFrame(quadro);
      scroller.removeEventListener("scroll", aoRolar);
      olho.disconnect();
    };
  }, [area, dependencia]);

  const irPara = (no, bloco = "start") => no.scrollIntoView({ behavior: "smooth", block: bloco });
  if (dias.length < 2) return null;

  return (
    <nav className="mapa" aria-label="Dias da conversa">
      {dias.flatMap((d) => [
        <button
          key={d.chave}
          type="button"
          aria-current={ativo === d.chave ? "true" : undefined}
          aria-label={`Ir para ${d.rotulo}`}
          onClick={() => irPara(d.no)}
        >
          <span>
            {d.rotulo}
            {d.conta ? <i style={s("font-style:normal;opacity:.55;margin-left:6px")}>{d.conta}</i> : null}
          </span>
        </button>,
        /* Os anexos só entram no mapa enquanto o dia deles está sendo lido: fora
           dele seriam uma lista, e é justo ali que o mapa precisa de detalhe. */
        ...(ativo === d.chave
          ? d.anexos.map((a) => (
              <button
                key={a.chave}
                type="button"
                data-sub="true"
                aria-label={`Ir para ${a.rotulo}`}
                onClick={() => irPara(a.no, "center")}
              >
                <span>{a.rotulo}</span>
              </button>
            ))
          : []),
      ])}
      {!naBase && (
        <button
          type="button"
          data-fim="true"
          aria-label="Ir para a última mensagem"
          onClick={() => area.current?.scrollTo({ top: area.current.scrollHeight, behavior: "smooth" })}
        >
          <span>
            última mensagem
            <Icone de={ICONES.baixo} tamanho={13} />
          </span>
        </button>
      )}
    </nav>
  );
}

/* ------------------------------------------------------------------- rolagem */

/**
 * A rolagem da trilha — a parte que decide quando a tela se move sozinha.
 *
 * Havia um efeito só: "mudou a quantidade de mensagens, vai para o fim". Ele
 * acertava um caso (chegou mensagem com a leitura no fim) e errava três: jogava
 * para hoje quem estava lendo o histórico, perdia o lugar quando o pedaço
 * anterior entrava por cima, e descia por cima do que a busca acabou de apontar.
 * As quatro respostas estão em `rolagem.js`; aqui elas encontram o DOM.
 */
function useRolagem(area, sentinela, mensagens, casoId, naoLidasDesde) {
  const [novas, setNovas] = useState(0);
  const [longe, setLonge] = useState(false);
  const anterior = useRef(retrato([], null));
  /* A medida de antes do histórico entrar. Guardada no clique/observador, e não
     no efeito: quando o efeito roda o DOM já cresceu e a diferença se perdeu. */
  const antesDeCrescer = useRef(null);

  const irAoFim = useCallback(
    (suave = true) => {
      const el = area.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: suave ? "smooth" : "auto" });
      setNovas(0);
    },
    [area],
  );

  const marcarAntesDeCrescer = useCallback(() => {
    const el = area.current;
    if (el) antesDeCrescer.current = { altura: el.scrollHeight, topo: el.scrollTop };
  }, [area]);

  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    const agora = retrato(mensagens, casoId);
    const mudou = oQueMudou(anterior.current, agora);
    anterior.current = agora;

    if (mudou === "caso") {
      /* Conversa nova pousa no fim sem animação — ou na linha de não lidas, que
         é onde a leitura realmente para. */
      const alvo = naoLidasDesde && el.querySelector(`[data-mensagem="${naoLidasDesde.id}"]`);
      if (alvo) alvo.scrollIntoView({ block: "center" });
      else el.scrollTop = el.scrollHeight;
      setNovas(0);
      return;
    }
    if (mudou === "comeco") {
      /* Devolve a rolagem à MESMA linha: a altura cresceu por cima, então o
         deslocamento é a diferença. Sem isto, buscar o histórico jogava quem lia
         para o meio de abril. */
      const antes = antesDeCrescer.current;
      if (antes) el.scrollTop = el.scrollHeight - antes.altura + antes.topo;
      antesDeCrescer.current = null;
      return;
    }
    if (mudou === "fim") {
      if (estaNaBase(el)) irAoFim();
      else setNovas((n) => n + 1);
    }
  }, [mensagens, casoId, naoLidasDesde, area, irAoFim]);

  /*
   * "A leitura está perto do fim?" — perguntado ao navegador, não medido a cada
   * quadro.
   *
   * Isto era um ouvinte de rolagem que lia `scrollHeight`, `scrollTop` e
   * `clientHeight` DUAS vezes por evento — e o navegador manda mais eventos de
   * rolagem do que quadros, cada leitura obrigando o layout a ficar pronto antes
   * da hora. Numa conversa longa era isso que engasgava a descida.
   *
   * A sentinela é um pedaço vazio no fim da trilha. O `IntersectionObserver`
   * avisa quando ela entra na vista, e a folga vira `rootMargin`: o navegador
   * faz a conta onde ela é de graça e só chama de volta na TRAVESSIA — duas ou
   * três vezes por rolagem inteira, em vez de sessenta.
   *
   * Duas margens, e não uma, de propósito: a pílula aparece a 400px do fim e só
   * se recolhe a 160px. Uma fronteira só, no meio de uma descida suave que a
   * atravessa devagar, acendia e apagava a pílula em sequência — a piscada.
   */
  useEffect(() => {
    const el = area.current;
    const marca = sentinela.current;
    if (!el || !marca) return undefined;
    const olhar = (folga, aoCruzar) => {
      const o = new IntersectionObserver((entradas) => aoCruzar(entradas[entradas.length - 1].isIntersecting), {
        root: el,
        rootMargin: `0px 0px ${folga}px 0px`,
      });
      o.observe(marca);
      return o;
    };
    /* Longe: apareceu passando de 400px do fim, sumiu voltando para dentro de
       160px. Entre as duas medidas a pílula não muda de ideia. */
    const surge = olhar(400, (perto) => perto || setLonge(true));
    const recolhe = olhar(160, (perto) => {
      if (!perto) return;
      setLonge(false);
      /* Chegou ao fim com o dedo: a conta de novas já foi lida. */
      setNovas(0);
    });
    return () => {
      surge.disconnect();
      recolhe.disconnect();
    };
  }, [area, sentinela]);

  return { novas, longe, irAoFim, marcarAntesDeCrescer };
}

/* ------------------------------------------------------------------- trilha */

/** A conversa rolando: histórico, bolhas, busca, seleção e o que está saindo. */
export default function Trilha() {
  const { caso, provas, equipe, etapas, antigas, antigasFim, pendentes, naoLidasDesde, presenca, selecionadas, apontada } =
    useEstado();
  const ocupado = useOcupado();
  /* A etapa vira NOME pelo schema, que é configurável em tempo de execução: o
     marco "Etapa → aguardando_documentos" mostraria a chave do banco. */
  const rotuloDaEtapa = (chave) => (etapas ?? []).find((e) => e.chave === chave)?.rotulo ?? String(chave ?? "");
  /* A prova também se marca de dentro do visor: a foto do holerite é decidida
     olhando a foto grande. O gancho é o mesmo da aba Anexos — o visor não pode
     mudar de comportamento conforme a porta por onde foi aberto. */
  const acao = useAcaoDeProva();
  const { carregarAntigas, apontarMensagem, selecionar } = useAcoes();
  const { ajustar, naConversa, procurarAberto } = useNavegacao();
  const celular = useCelular();
  const area = useRef(null);
  /* O fim da trilha, para o observador de travessia (ver `useRolagem`). */
  const sentinela = useRef(null);
  const pasta = caso?.pasta ?? null;
  const casoId = caso?.registro?.id ?? null;

  /*
   * A conversa da tela é a soma de três origens — ver `juntarConversa`.
   *
   * O eco é filtrado pelo caso: o que falhou continua guardado enquanto a pessoa
   * vai ver outra conversa e volta. Jogá-lo fora na troca perderia o texto de uma
   * mensagem que não saiu — exatamente o que o eco existe para não perder.
   */
  const mensagens = useMemo(
    () =>
      juntarConversa(
        antigas,
        caso?.mensagens ?? [],
        (pendentes ?? []).filter((e) => e.record_id === casoId),
      ),
    [antigas, caso?.mensagens, pendentes, casoId],
  );
  const citadas = useMemo(() => indiceDeCitadas(mensagens), [mensagens]);
  const blocos = useMemo(() => agrupar(mensagens, caso, equipe), [mensagens, caso, equipe]);
  /* Os blocos embrulhados por dia: é o embrulho que segura o chip da data no
     alto enquanto o dia está na tela (ver `Divisor`). */
  const dias = useMemo(() => porDiaDaConversa(blocos), [blocos]);
  const { novas, longe, irAoFim, marcarAntesDeCrescer } = useRolagem(area, sentinela, mensagens, casoId, naoLidasDesde);

  /* Quantas mensagens tem cada dia — o minimapa mostra o número ao lado do dia,
     que é o que diferencia "o dia do atendimento" de "o dia do bom dia". */
  const porDia = useMemo(() => {
    const conta = {};
    /* Pelo dia DO LEITOR, o mesmo que `agrupar` usa para abrir o divisor: com o
       corte cru do texto (UTC) a contagem do minimapa saía de outro dia. */
    for (const m of mensagens) {
      const d = diaDe(m.quando);
      conta[d] = (conta[d] ?? 0) + 1;
    }
    return conta;
  }, [mensagens]);

  /* A lista de anexos da conversa inteira, na ordem em que apareceram: é ela que
     o visor recebe, para a seta andar de anexo em anexo mesmo abrindo pela bolha. */
  const anexos = useMemo(
    () =>
      mensagens
        .filter((m) => m.arquivo || m.anexo)
        .map((m) => ({ id: m.id, nome: m.arquivo || m.anexo, url: urlDoAnexo(pasta, m.arquivo || m.anexo), quando: m.quando })),
    [mensagens, pasta],
  );
  const anexoDe = useCallback((m) => anexos.find((a) => a.id === m.id) ?? null, [anexos]);
  const aoAbrirPeticao = useCallback((fatoId) => ajustar({ peticao: true, fato: fatoId ?? null }), [ajustar]);

  /* As bolhas que chegaram DEPOIS desta tela montar. É o que decide quem entra
     animado: sem a marca, toda releitura da trilha re-animava a conversa inteira. */
  const vistas = useRef(new Set());
  const daTelaAnterior = useRef(null);
  if (daTelaAnterior.current !== casoId) {
    daTelaAnterior.current = casoId;
    vistas.current = new Set(mensagens.map((m) => m.id));
  }
  /*
   * Anima só o que CHEGA — nunca o que o próprio escritório mandou.
   *
   * A mensagem que sai daqui já apareceu na tela como eco, na hora do Enter.
   * Quando a versão do servidor toma o lugar dela, ela é "nova" para o React (id
   * diferente) e re-animava: a bolha piscava e a rolagem dava um pulo, no exato
   * momento em que a pessoa está lendo o que acabou de escrever.
   */
  const novaAqui = (bloco) => bloco.mensagens.some((m) => !vistas.current.has(m.id) && m.direcao !== "saida");
  useEffect(() => {
    for (const m of mensagens) vistas.current.add(m.id);
  }, [mensagens]);

  /* O "digitando…" nasce embaixo da última bolha: quem está no fim tem de vê-lo
     sem rolar, e quem está lendo o histórico não pode ser arrastado por ele. */
  const digitando = Boolean(presencaEmPalavras(presenca)?.vivo);
  useEffect(() => {
    if (digitando && estaNaBase(area.current)) irAoFim();
  }, [digitando, irAoFim]);

  /* Rolar até a mensagem apontada (busca, citação) e deixar o realce falar. */
  useEffect(() => {
    if (!apontada) return;
    area.current?.querySelector(`[data-mensagem="${apontada}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [apontada]);

  const selecao = useMemo(
    () => (selecionadas ? { ativa: true, marcadas: selecionadas, alternar: selecionar } : null),
    [selecionadas, selecionar],
  );

  const buscarAntigas = useCallback(() => {
    marcarAntesDeCrescer();
    carregarAntigas();
  }, [carregarAntigas, marcarAntesDeCrescer]);

  /*
   * As reuniões do caso, encaixadas na conversa pela hora em que aconteceram.
   *
   * O consumo é por bloco: cada bloco desenha antes de si as gravações que são
   * mais antigas que a primeira mensagem dele. O que sobrar depois do último
   * bloco sai no fim — reunião de hoje numa conversa que parou ontem.
   */
  const porConsumir = useRef([]);
  porConsumir.current = [...(caso?.gravacoes ?? [])].sort((a, b) => String(a.criado_em).localeCompare(String(b.criado_em)));
  const reunioesAntesDe = (bloco) => {
    const limite = String(bloco.mensagens[0]?.quando ?? "");
    const saem = [];
    while (porConsumir.current.length && String(porConsumir.current[0].criado_em ?? "") <= limite)
      saem.push(porConsumir.current.shift());
    return saem;
  };

  /*
   * Os marcos entram pelo mesmo caminho das reuniões: pelo relógio, entre os
   * blocos. É o histórico do caso costurado na conversa — contrato enviado,
   * documento assinado, documento aprovado, peça na esteira, protocolo — e o
   * que ficava só em `events`, que ninguém abre.
   */
  const marcos = useRef([]);
  marcos.current = marcosDaTrilha(caso?.trilha ?? [], rotuloDaEtapa);
  const marcosAntesDe = (bloco) => {
    const limite = String(bloco.mensagens[0]?.quando ?? "");
    const saem = [];
    while (marcos.current.length && String(marcos.current[0].quando ?? "") <= limite) saem.push(marcos.current.shift());
    return saem;
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {(procurarAberto || naConversa) && <BuscaNaConversa mensagens={mensagens} />}
      {/* A moldura do que rola. O contêiner de baixo é lido e escrito à mão
          (`rolagem.js`, o minimapa, "estou no fim") — por isso ele NÃO é um
          `ScrollArea`: um viewport interno mudaria de quem é o `scrollTop`. */}
      <div className="relative min-h-0 flex-1">
        <SelecaoDeMensagens />
        {/* `role="log"` é exatamente esta área: região que cresce por baixo, e o
            leitor de tela anuncia só o que ENTRA depois da montagem — não as 200
            bolhas já desenhadas. Sem ele, o cliente respondia e quem opera por
            leitor de tela não recebia aviso nenhum do dado mais perecível da mesa.
            `role="log"` já é cortês (polite); nada de `aria-live` além disto. */}
        <div
          ref={area}
          role="log"
          aria-label="Conversa"
          aria-relevant="additions"
          style={s("height:100%;overflow-y:auto;overflow-x:hidden;padding:20px 18px 6px")}
        >
          <div>
            {mensagens.length > 0 && (
              <TopoDoHistorico area={area} temMais={!antigasFim} buscando={ocupado === "antigas"} aoBuscar={buscarAntigas} />
            )}
            {/* Um embrulho por DIA: é o que segura o chip da data no alto
                enquanto aquele dia está na tela, e o que faz o dia seguinte
                empurrá-lo para fora ao chegar. */}
            {dias.map((d) => (
              <div key={d.chave}>
                <Divisor diaIso={d.dia} quantas={porDia[d.dia]} />
                {d.blocos.map((bloco) => (
                  <div key={bloco.chave}>
                    {naoLidasDesde?.id === bloco.mensagens[0]?.id && <DivisorDeNaoLidas quantas={naoLidasDesde.quantas} />}
                    {/* As reuniões entram na hora em que aconteceram, entre as mensagens. */}
                    {reunioesAntesDe(bloco).map((g) => (
                      <CartaoDeReuniao
                        key={`g-${g.id}`}
                        gravacao={g}
                        aoAbrir={() => ajustar({ painel: "reunioes", gravacao: g.id })}
                      />
                    ))}
                    {marcosAntesDe(bloco).map((m) => (
                      <MarcoDoCaso
                        key={m.id}
                        marco={m.marco}
                        quando={m.quando}
                        autor={m.autor}
                        aoAbrirPeca={m.marco.acao === "peca" ? () => ajustar({ peticao: true }) : undefined}
                      />
                    ))}
                    <Bloco
                      bloco={bloco}
                      celular={celular}
                      anexoDe={anexoDe}
                      anexos={anexos}
                      provas={provas}
                      acao={acao}
                      aoAbrirPeticao={aoAbrirPeticao}
                      citadas={citadas}
                      termo={naConversa}
                      apontada={apontada}
                      selecao={selecao}
                      aoIr={apontarMensagem}
                      nova={novaAqui(bloco)}
                    />
                  </div>
                ))}
              </div>
            ))}
            {porConsumir.current.map((g) => (
              <CartaoDeReuniao key={`g-${g.id}`} gravacao={g} aoAbrir={() => ajustar({ painel: "reunioes", gravacao: g.id })} />
            ))}
            <LinhaDePresenca presenca={presenca} nome={caso?.registro?.nome?.split(" ")[0] ?? "O cliente"} />
            {!mensagens.length && (
              <div className="p-[30px] text-center text-corpo text-muted-foreground">
                Nenhuma mensagem ainda. Escreva abaixo para começar.
              </div>
            )}
            {/* A sentinela do fim: sem altura, sem pintura. É o que o
                `IntersectionObserver` observa para dizer se a leitura chegou
                perto do fim, no lugar de medir a rolagem a cada quadro. */}
            <div ref={sentinela} aria-hidden="true" className="h-px w-full" />
          </div>
        </div>
        <PilulaDeNovas quantas={novas} mostrar={novas > 0 || longe} aoTocar={() => irAoFim()} />
        <Minimapa area={area} dependencia={blocos} />
      </div>
    </div>
  );
}
